import { initializeApp } from "firebase/app";
import { initializeAppCheck, ReCaptchaEnterpriseProvider } from "firebase/app-check";
import { getAuth } from "firebase/auth";
import { getFirestore } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { getFunctions } from "firebase/functions";
import { getAnalytics, initializeAnalytics } from "firebase/analytics";
import { getConsent } from "./utils/consent";

// Two environments, selected at BUILD time by REACT_APP_ENV. Production is
// the default on purpose: a build with no env set must be the safe, normal
// thing, because forgetting the variable is the likely mistake and shipping
// a staging-pointed bundle to tastr.app would be the expensive one.
//
//   npm run build            -> production  (tastr-451700)
//   npm run build:staging    -> staging     (tastr-staging)
//
// None of these values are secret. A Firebase web config identifies the
// project; it authorizes nothing. firestore.rules is what decides access.
const PROD_CONFIG = {
  apiKey: "AIzaSyCeuXRAH8Zml1rN4-O2LFFRjvpFco0BRHM",
  // Custom domain: sign-in popup helpers are served from tastr.app itself
  // (Firebase Hosting serves the reserved /__/auth/* paths on custom domains),
  // which avoids third-party-storage popup issues in Safari/Chrome.
  authDomain: "tastr.app",
  projectId: "tastr-451700",
  storageBucket: "tastr-451700.firebasestorage.app",
  messagingSenderId: "898814739471",
  appId: "1:898814739471:web:73d8203a0870927a130f7a",
  measurementId: "G-6SCF6S5NMX"
};

// No custom domain and no measurementId: staging has no analytics property,
// and getAnalytics() without one is a no-op rather than an error.
const STAGING_CONFIG = {
  apiKey: "AIzaSyC1Auxk1TnCwKFkhf6trYhkDIMYoL50_es",
  authDomain: "tastr-staging.firebaseapp.com",
  projectId: "tastr-staging",
  storageBucket: "tastr-staging.firebasestorage.app",
  messagingSenderId: "595440001516",
  appId: "1:595440001516:web:f6bf18cfd28112b08da0c9"
};

export const IS_STAGING = process.env.REACT_APP_ENV === 'staging';
const firebaseConfig = IS_STAGING ? STAGING_CONFIG : PROD_CONFIG;

if (IS_STAGING && typeof console !== 'undefined') {
  // Loud on purpose. The two environments look identical, and the failure
  // mode this prevents is doing destructive "testing" against real users'
  // data while believing you are in staging -- or the reverse.
  console.warn('%cTASTR STAGING — not production data', 'font-weight:bold');
}

const app = initializeApp(firebaseConfig);

// ---- App Check ----------------------------------------------------------
// Attests that a request came from THIS app rather than from a browser
// console or a script. Security rules are still the real gate -- App Check
// doesn't decide who may read what, it decides who may knock at all. It
// matters here because the browser talks straight to Firestore, so every
// rules mistake is directly reachable by anyone curious; every issue found
// in the 2026-08 security scans was found exactly that way.
//
// SHIPPED IN MONITORING MODE ON PURPOSE. Registering the app does NOT
// enforce anything -- unverified requests still succeed, and the console
// shows what proportion of traffic is passing. Enforcement is a separate
// per-service switch in the Firebase console, and it must NOT be turned on
// until those metrics show real traffic is verified. Turning it on early
// locks out real users, which is precisely why this sat deferred so long.
//
// The site key is public by design (same as apiKey above) -- it identifies
// the reCAPTCHA key, it doesn't authorize anything.
//
// SETUP: Firebase console > App Check > Apps > register this web app with
// reCAPTCHA Enterprise, then paste the site key below. Until it is set,
// this block no-ops and the app behaves exactly as it does today.
const APP_CHECK_SITE_KEY = '';

// Localhost has no reCAPTCHA attestation, so dev builds need a debug token:
// this prints one to the console on first run, which you register under
// App Check > Apps > (app) > Manage debug tokens. Guarded by NODE_ENV so a
// production build can never ship a debug bypass.
if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
  // eslint-disable-next-line no-restricted-globals
  self.FIREBASE_APPCHECK_DEBUG_TOKEN = true;
}

if (APP_CHECK_SITE_KEY && typeof window !== 'undefined') {
  try {
    initializeAppCheck(app, {
      provider: new ReCaptchaEnterpriseProvider(APP_CHECK_SITE_KEY),
      isTokenAutoRefreshEnabled: true
    });
  } catch (err) {
    // Never let attestation setup break the app. In monitoring mode a
    // missing token costs nothing; after enforcement it would surface as
    // permission errors, which is the signal you want rather than a blank
    // screen at import time.
    console.error('App Check failed to initialize:', err);
  }
}

export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
export const functions = getFunctions(app);

// Analytics only works in browser, not during SSR — and never before the
// user consents to more than essential cookies. Called at load when consent
// was already given, or by the cookie banner on "Accept all".
let analytics = null;
export function enableAnalytics() {
  if (analytics || typeof window === 'undefined') return analytics;
  try {
    // send_page_view: false makes usePageViews (App.js) the SINGLE source of
    // page_view events. Without it GA fires one automatically on load AND
    // our effect fires on mount, so the first view of every session counts
    // twice -- and the automatic one reads document.title before React has
    // set it, so it lands under the wrong page name too.
    // The other half of this is a console setting: enhanced measurement's
    // "Page changes based on browser history events" must stay OFF.
    analytics = initializeAnalytics(app, { config: { send_page_view: false } });
  } catch (e) {
    // initializeAnalytics throws if analytics was already initialized for
    // this app; fall back to the existing instance rather than losing it.
    try {
      analytics = getAnalytics(app);
    } catch (_) {
      console.log('Analytics not available');
    }
  }
  return analytics;
}
if (getConsent() === 'all') enableAnalytics();
export { analytics };
export default app;
